// ============================================================================
// Approval Stage Wizard - Constants
// ============================================================================

import type { TimesheetField, Operator, StageOption } from './types';

// ============================================================================
// Wizard Steps Configuration
// ============================================================================

export const WIZARD_STEPS = [
  {
    id: 1,
    title: 'Stage Details & Approver',
    description: 'Stage name and who approves',
    icon: 'FileText',
  },
  {
    id: 2,
    title: 'Smart Rules',
    description: 'Routing rules and actions',
    icon: 'Zap',
  },
  {
    id: 3,
    title: 'Notifications',
    description: 'Email and system notifications',
    icon: 'Bell',
  },
];

// ============================================================================
// Operators by Type
// ============================================================================

const NUMBER_OPERATORS: Operator[] = [
  { id: '>', label: 'is greater than', applicableTypes: ['number'] },
  { id: '<', label: 'is less than', applicableTypes: ['number'] },
  { id: '=', label: 'equals', applicableTypes: ['number'] },
  { id: '>=', label: 'is greater than or equal to', applicableTypes: ['number'] },
  { id: '<=', label: 'is less than or equal to', applicableTypes: ['number'] },
  { id: '!=', label: 'is not equal to', applicableTypes: ['number'] },
];

const TEXT_OPERATORS: Operator[] = [
  { id: '=', label: 'equals', applicableTypes: ['text'] },
  { id: '!=', label: 'not equals', applicableTypes: ['text'] },
  { id: 'contains', label: 'contains', applicableTypes: ['text'] },
  { id: 'startsWith', label: 'starts with', applicableTypes: ['text'] },
  { id: 'endsWith', label: 'ends with', applicableTypes: ['text'] },
];

const BOOLEAN_OPERATORS: Operator[] = [
  { id: 'isTrue', label: 'is true', applicableTypes: ['boolean'] },
  { id: 'isFalse', label: 'is false', applicableTypes: ['boolean'] },
];

const DATE_OPERATORS: Operator[] = [
  { id: 'before', label: 'is before', applicableTypes: ['date'] },
  { id: 'after', label: 'is after', applicableTypes: ['date'] },
  { id: 'on', label: 'is on', applicableTypes: ['date'] },
];

// ============================================================================
// Timesheet Fields
// ============================================================================

export const TIMESHEET_FIELDS: TimesheetField[] = [
  {
    id: 'TST_TOTAL_HOURS',
    label: 'Total Hours',
    type: 'number',
    operators: NUMBER_OPERATORS,
  },
  {
    id: 'TST_OVERTIME_HOURS',
    label: 'Overtime Hours',
    type: 'number',
    operators: NUMBER_OPERATORS,
  },
  {
    id: 'TST_BILLABLE_HOURS',
    label: 'Billable Hours',
    type: 'number',
    operators: NUMBER_OPERATORS,
  },
  {
    id: 'TST_RESOURCE',
    label: 'Resource Code',
    type: 'text',
    operators: TEXT_OPERATORS,
  },
  {
    id: 'PROJECT_TYPE',
    label: 'Project Type',
    type: 'text',
    operators: TEXT_OPERATORS,
  },
  {
    id: 'PROJECT_CODE',
    label: 'Project Code',
    type: 'text',
    operators: TEXT_OPERATORS,
  },
  {
    id: 'CLIENT_CODE',
    label: 'Client Code',
    type: 'text',
    operators: TEXT_OPERATORS,
  },
  {
    id: 'DEPARTMENT',
    label: 'Department',
    type: 'text',
    operators: TEXT_OPERATORS,
  },
  {
    id: 'IS_BILLABLE',
    label: 'Is Billable',
    type: 'boolean',
    operators: BOOLEAN_OPERATORS,
  },
  {
    id: 'HAS_OVERTIME',
    label: 'Has Overtime',
    type: 'boolean',
    operators: BOOLEAN_OPERATORS,
  },
  {
    id: 'SUBMIT_DATE',
    label: 'Submit Date',
    type: 'date',
    operators: DATE_OPERATORS,
  },
];

// ============================================================================
// Rule Actions
// ============================================================================

export const RULE_ACTIONS = {
  preStage: [
    { id: 'continue', label: 'Continue to this stage' },
    { id: 'skip', label: 'Skip this stage' },
    { id: 'gotoStage', label: 'Jump to stage' },
    { id: 'gotoEnd', label: 'Fully approve (end workflow)' },
    { id: 'reject', label: 'Reject' },
  ],
  postStage: [
    { id: 'continue', label: 'Continue to next stage' },
    { id: 'gotoStage', label: 'Jump to stage' },
    { id: 'gotoEnd', label: 'Fully approve (end workflow)' },
    { id: 'reject', label: 'Reject' },
  ],
};

// ============================================================================
// Mock Data - Available Stages
// ============================================================================

export const MOCK_AVAILABLE_STAGES: StageOption[] = [
  { id: 'stage-1', name: 'Line Manager Approval', order: 1 },
  { id: 'stage-2', name: 'Department Head Approval', order: 2 },
  { id: 'stage-3', name: 'Finance Review', order: 3 },
  { id: 'stage-4', name: 'Executive Approval', order: 4 },
];

// ============================================================================
// Mock Data - Users (for specific person selection)
// ============================================================================

export const MOCK_USERS = [
  { id: 'user-1', name: 'John Smith', role: 'Manager' },
  { id: 'user-2', name: 'Sarah Johnson', role: 'Department Head' },
  { id: 'user-3', name: 'Michael Brown', role: 'Finance Director' },
  { id: 'user-4', name: 'Emily Davis', role: 'CEO' },
  { id: 'user-5', name: 'David Wilson', role: 'Project Manager' },
];

// ============================================================================
// Timeout Action Options
// ============================================================================

export const TIMEOUT_ACTIONS = [
  { id: 'escalate', label: 'Escalate to' },
  { id: 'autoApprove', label: 'Auto-approve' },
  { id: 'autoReject', label: 'Auto-reject' },
];

// ============================================================================
// Escalation Options
// ============================================================================

export const ESCALATION_OPTIONS = [
  { id: 'DirectManager', label: 'Direct Manager' },
  { id: 'DepartmentHead', label: 'Department Head' },
  { id: 'ProjectManager', label: 'Project Manager' },
  { id: 'SpecificPerson', label: 'Specific Person' },
];

// ============================================================================
// Notification Types
// ============================================================================

export const NOTIFICATION_TYPES = [
  { id: 'email', label: 'Send Email', icon: 'Mail' },
  { id: 'system', label: 'System Notification', icon: 'Bell' },
  { id: 'webhook', label: 'Webhook', icon: 'Link' },
  { id: 'log', label: 'Log Message', icon: 'FileText' },
];

// ============================================================================
// Email Recipients
// ============================================================================

export const EMAIL_RECIPIENTS = [
  { id: 'employee', label: 'Employee (timesheet owner)' },
  { id: 'approver', label: 'Current Approver' },
  { id: 'nextApprover', label: 'Next Approver' },
  { id: 'manager', label: 'Direct Manager' },
  { id: 'departmentHead', label: 'Department Head' },
  { id: 'specific', label: 'Specific Person' },
];
